import { useCallback, useEffect, useState } from "react";
import { AlertCircle, ArrowLeft, ArrowRight, Clock3, RefreshCw, TrendingUp, Upload, Users } from "lucide-react";
import CustomerImportPage from "@/components/admin_dashboard/CustomerImportPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchAllSupabaseRows } from "@/lib/supabasePagination";

const REVENUE_PER_DORMANT_CUSTOMER = 200;

interface Props {
  nextStep: () => void;
  prevStep: () => void;
  onCountChange?: (count: number) => void;
}

const ConnectorsStep = ({ nextStep, prevStep, onCountChange }: Props) => {
  const { businessId } = useAuth();
  const [importOpen, setImportOpen] = useState(false);
  const [customerMetrics, setCustomerMetrics] = useState<{
    total: number;
    dormant: number;
    recoverableRevenue: number;
  } | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  const loadCount = useCallback(async () => {
    if (!businessId) {
      setCustomerMetrics({ total: 0, dormant: 0, recoverableRevenue: 0 });
      setMetricsError(null);
      setMetricsLoading(false);
      onCountChange?.(0);
      return;
    }

    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const rows = await fetchAllSupabaseRows(async (from, to) => {
        const { data, error } = await supabase
          .from("customers")
          .select("customer_id, last_booking_at")
          .eq("business_id", businessId)
          .order("customer_id", { ascending: true })
          .range(from, to);
        return { data, error };
      });

      const dormantCutoff = Date.now() - 60 * 24 * 60 * 60 * 1000;
      const dormant = rows.filter((customer) => {
        if (!customer.last_booking_at) return true;
        const lastEngagedAt = new Date(customer.last_booking_at).getTime();
        return Number.isNaN(lastEngagedAt) || lastEngagedAt < dormantCutoff;
      }).length;
      const nextMetrics = {
        total: rows.length,
        dormant,
        recoverableRevenue: dormant * REVENUE_PER_DORMANT_CUSTOMER,
      };

      setCustomerMetrics(nextMetrics);
      onCountChange?.(nextMetrics.total);
    } catch (error) {
      console.error("[onboarding] failed to load customer import metrics", error);
      setCustomerMetrics(null);
      setMetricsError(error instanceof Error ? error.message : "Customer totals could not be loaded.");
    } finally {
      setMetricsLoading(false);
    }
  }, [businessId, onCountChange]);

  useEffect(() => {
    void loadCount();
  }, [loadCount]);

  const finishImport = () => {
    setImportOpen(false);
    void loadCount();
  };

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="space-y-6">
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-8 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Upload className="h-6 w-6" />
        </span>
        <h2 className="mt-4 text-base font-semibold text-foreground">Import customers</h2>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
          Upload your customer list, match its columns to Dibs, review the data,
          and confirm the import before anything is created.
        </p>
        <Button
          type="button"
          size="sm"
          className="mt-5 h-7 gap-1.5 px-2.5"
          disabled={!businessId}
          onClick={() => setImportOpen(true)}
        >
          <Upload className="h-3.5 w-3.5" />
          Import customers
        </Button>
        <p className="mt-3 text-xs text-muted-foreground">
          CSV or Excel files · Up to 5,000 rows
        </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {[
            {
              Icon: Users,
              value: customerMetrics?.total.toLocaleString("en-US"),
              label: "Customers currently imported",
            },
            {
              Icon: Clock3,
              value: customerMetrics?.dormant.toLocaleString("en-US"),
              label: "Customers not engaged in the past 60 days",
            },
            {
              Icon: TrendingUp,
              value: customerMetrics
                ? `$${customerMetrics.recoverableRevenue.toLocaleString("en-US")}`
                : undefined,
              label: `Estimated recoverable revenue ($${REVENUE_PER_DORMANT_CUSTOMER} per dormant customer)`,
            },
          ].map(({ Icon, value, label }) => (
            <div key={label} className="rounded-xl border border-border bg-card p-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </span>
              <p className="mt-4 text-xl font-semibold leading-none text-foreground">
                {metricsLoading ? "—" : value ?? "—"}
              </p>
              <p className="mt-2 text-[11px] leading-snug text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
        {metricsError && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            <span className="flex min-w-0 items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Customer totals could not be loaded. No zero values have been assumed.
            </span>
            <Button type="button" variant="outline" size="sm" className="h-7 shrink-0" onClick={() => void loadCount()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Retry
            </Button>
          </div>
        )}
      </div>

      <div className="mt-auto flex justify-between">
        <Button type="button" variant="outline" size="sm" onClick={prevStep}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back
        </Button>
        <Button type="button" size="sm" onClick={nextStep}>
          Continue <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="h-[min(780px,calc(100dvh-2rem))] w-[calc(100vw-2rem)] max-w-6xl gap-0 overflow-hidden p-0 [&>button]:hidden">
          <DialogTitle className="sr-only">Import customers</DialogTitle>
          <CustomerImportPage
            embedded
            onCancel={() => setImportOpen(false)}
            onComplete={finishImport}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConnectorsStep;
